import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, LogOut, RefreshCw, Plus, Pencil, Trash2,
  Globe, Building2, Briefcase, X, Upload, Loader,
  MessageSquare, Users,
} from 'lucide-react';
import { fetchAllClients, createClient, updateClient, deleteClient } from '../api/clients';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TYPE_META = {
  industrial: { label: 'Industrial', bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  sme:        { label: 'SME',        bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
};

const EMPTY_FORM = { name: '', website: '', type: 'industrial', order: '0', active: true };

/* ═══════════════════════════════════════════════════════════════════════════
   MODAL — Add / Edit Client
═══════════════════════════════════════════════════════════════════════════ */
function ClientModal({ client, token, onSave, onClose }) {
  const [form,    setForm]    = useState(client ? {
    name:    client.name,
    website: client.website || '',
    type:    client.type,
    order:   String(client.order ?? 0),
    active:  client.active ?? true,
  } : EMPTY_FORM);
  const [file,     setFile]     = useState(null);
  const [preview,  setPreview]  = useState(client ? `${API_BASE}${client.logoPath}` : null);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const applyFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleFile = (e) => applyFile(e.target.files[0]);

  const handleDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
  const handleDrop      = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    applyFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client && !file) { setError('Please upload a logo.'); return; }
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('name',    form.name.trim());
      fd.append('website', form.website.trim());
      fd.append('type',    form.type);
      fd.append('order',   form.order || '0');
      fd.append('active',  String(form.active));
      if (file) fd.append('logo', file);

      const saved = client
        ? await updateClient(client._id, fd, token)
        : await createClient(fd, token);
      onSave(saved, !!client);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={M.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={M.card}>

        {/* Header */}
        <div style={M.header}>
          <div>
            <p style={M.headerLabel}>{client ? 'Edit Client' : 'Add New Client'}</p>
          </div>
          <button onClick={onClose} style={M.closeBtn}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

          {/* Logo upload */}
          <div>
            <label style={M.label}>Logo *</label>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${dragging ? '#2563eb' : '#e2e8f0'}`,
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center',
                cursor: 'pointer',
                background: dragging ? '#eff6ff' : '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              {preview ? (
                <img src={preview} alt="Logo preview" style={{ maxHeight: '64px', maxWidth: '180px', objectFit: 'contain' }} />
              ) : (
                <>
                  <Upload size={24} color={dragging ? '#2563eb' : '#94a3b8'} />
                  <span style={{ fontSize: '13px', color: dragging ? '#2563eb' : '#64748b', fontWeight: dragging ? 600 : 400 }}>
                    {dragging ? 'Drop to upload' : 'Click or drag & drop logo here'}
                  </span>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>PNG, JPG, WEBP, SVG — max 5 MB</span>
                </>
              )}
              {preview && (
                <span style={{ fontSize: '11px', color: '#2563eb', fontWeight: 600 }}>
                  Click to change
                </span>
              )}
            </div>
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.avif" style={{ display: 'none' }} onChange={handleFile} />
          </div>

          {/* Name */}
          <div>
            <label style={M.label}>Company Name *</label>
            <input
              required
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Air Master Equipements Emirates LLC"
              style={M.input}
            />
          </div>

          {/* Website */}
          <div>
            <label style={M.label}>Website URL</label>
            <input
              value={form.website}
              onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
              placeholder="https://example.com"
              style={M.input}
            />
          </div>

          {/* Type */}
          <div>
            <label style={M.label}>Client Type *</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              {Object.entries(TYPE_META).map(([key, meta]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, type: key }))}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: `2px solid ${form.type === key ? meta.color : '#e2e8f0'}`,
                    background: form.type === key ? meta.bg : '#f8fafc',
                    color: form.type === key ? meta.color : '#64748b',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.15s',
                  }}
                >
                  {key === 'industrial' ? <Building2 size={15} /> : <Briefcase size={15} />}
                  {meta.label}
                </button>
              ))}
            </div>
          </div>

          {/* Order + Active row */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={M.label}>Display Order</label>
              <input
                type="number"
                min="0"
                value={form.order}
                onChange={e => setForm(f => ({ ...f, order: e.target.value }))}
                style={M.input}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={M.label}>Visible</label>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  border: 'none',
                  background: form.active ? '#2563eb' : '#cbd5e1',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background 0.2s',
                  marginTop: '4px',
                }}
              >
                <span style={{
                  position: 'absolute',
                  top: '4px',
                  left: form.active ? '28px' : '4px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '10px',
                  background: '#fff',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
            </div>
          </div>

          {error && (
            <p style={{ margin: 0, fontSize: '13px', color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '10px 14px' }}>
              {error}
            </p>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
            <button type="button" onClick={onClose} style={M.cancelBtn}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...M.saveBtn, opacity: saving ? 0.7 : 1 }}>
              {saving ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : null}
              {saving ? 'Saving…' : client ? 'Save Changes' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DELETE CONFIRM DIALOG
═══════════════════════════════════════════════════════════════════════════ */
function DeleteDialog({ client, token, onDelete, onClose }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteClient(client._id, token);
      onDelete(client._id);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div style={M.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...M.card, maxWidth: '400px' }}>
        <div style={M.header}>
          <p style={M.headerLabel}>Delete Client</p>
          <button onClick={onClose} style={M.closeBtn}><X size={16} /></button>
        </div>
        <div style={{ padding: '24px' }}>
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '20px', lineHeight: 1.5 }}>
            Are you sure you want to delete <strong>{client.name}</strong>? This action cannot be undone.
          </p>
          {error && <p style={{ fontSize: '13px', color: '#dc2626', marginBottom: '16px' }}>{error}</p>}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={M.cancelBtn}>Cancel</button>
            <button onClick={handleDelete} disabled={deleting} style={{ ...M.saveBtn, background: '#dc2626', opacity: deleting ? 0.7 : 1 }}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ADMIN CLIENTS PAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function AdminClients() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('adminToken');

  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modal,    setModal]    = useState(null); // null | 'add' | client-object
  const [deleting, setDeleting] = useState(null); // null | client-object

  useEffect(() => { if (!token) navigate('/admin/login'); }, [token, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllClients(token);
      setClients(data);
    } catch (err) {
      if (err.message?.includes('401') || err.message?.includes('403')) {
        sessionStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const filtered = clients.filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase());
    const matchType   = !typeFilter || c.type === typeFilter;
    return matchSearch && matchType;
  });

  const stats = {
    total:      clients.length,
    industrial: clients.filter(c => c.type === 'industrial').length,
    sme:        clients.filter(c => c.type === 'sme').length,
    active:     clients.filter(c => c.active).length,
  };

  const handleSaved = (saved, isEdit) => {
    if (isEdit) {
      setClients(prev => prev.map(c => c._id === saved._id ? saved : c));
    } else {
      setClients(prev => [...prev, saved]);
    }
    setModal(null);
  };

  const handleDeleted = (id) => {
    setClients(prev => prev.filter(c => c._id !== id));
    setDeleting(null);
  };

  const S = styles;

  return (
    <div style={S.page}>

      {/* ── NAVBAR ── */}
      <nav style={S.nav}>
        <div style={S.navInner}>
          <div style={S.navLeft}>
            <img src="/tuly.png" alt="Tuly" style={{ height: '24px', width: 'auto' }} />
            <div style={S.navDivider} />
            <span style={S.navLabel}>Admin</span>
            <span style={S.navChip}>Clients Manager</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => navigate('/admin/leads')} style={{ ...S.iconBtn, display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
              <Users size={14} color="#64748b" /> Leads
            </button>
            <button onClick={() => navigate('/admin/chats')} style={{ ...S.iconBtn, display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
              <MessageSquare size={14} color="#64748b" /> Chats
            </button>
            <button onClick={load} style={S.iconBtn} title="Refresh">
              <RefreshCw size={15} color="#94a3b8" />
            </button>
            <button
              onClick={() => { sessionStorage.removeItem('adminToken'); navigate('/admin/login'); }}
              style={S.logoutBtn}
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </nav>

      <div style={S.content}>

        {/* ── STATS ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '24px' }}>
          {[
            { label: 'Total Clients',  value: stats.total,      color: '#0a0f1e', border: '#e2e8f0' },
            { label: 'Industrial',     value: stats.industrial,  color: '#2563eb', border: '#bfdbfe' },
            { label: 'SME',            value: stats.sme,         color: '#ca8a04', border: '#fde68a' },
            { label: 'Active',         value: stats.active,      color: '#16a34a', border: '#bbf7d0' },
          ].map(item => (
            <div key={item.label} style={{ background: '#fff', border: `1px solid ${item.border}`, borderRadius: '4px', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <p style={{ margin: 0, fontSize: '26px', fontWeight: 800, color: item.color, letterSpacing: '-0.02em' }}>{item.value}</p>
              <p style={{ margin: '3px 0 0', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{item.label}</p>
            </div>
          ))}
        </div>

        {/* ── TOOLBAR ── */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '12px 16px', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: '180px' }}>
            <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search clients…"
              style={{ width: '100%', padding: '8px 32px 8px 32px', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '13px', color: '#0f172a', background: '#f8fafc', fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 0 }}>
                <X size={13} />
              </button>
            )}
          </div>

          {/* Type filter */}
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '13px', color: '#374151', background: '#f8fafc', fontFamily: 'inherit', cursor: 'pointer' }}
          >
            <option value="">All Types</option>
            <option value="industrial">Industrial</option>
            <option value="sme">SME</option>
          </select>

          {/* Add button */}
          <button
            onClick={() => setModal('add')}
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 18px', background: '#0a0f1e', color: '#fff', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.03em', marginLeft: 'auto', whiteSpace: 'nowrap' }}
          >
            <Plus size={15} /> Add Client
          </button>
        </div>

        {/* ── TABLE ── */}
        <div style={S.tableWrap}>
          {loading ? (
            <div style={S.loading}>
              <Loader size={20} color="#2563eb" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ color: '#64748b', fontSize: '13px' }}>Loading clients…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div style={S.empty}>
              <Users size={36} color="#e2e8f0" />
              <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '12px' }}>No clients found</p>
              <button onClick={() => setModal('add')} style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#0a0f1e', color: '#fff', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                <Plus size={14} /> Add First Client
              </button>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Logo</th>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Website</th>
                  <th style={S.th}>Order</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(client => {
                  const meta = TYPE_META[client.type] || TYPE_META.industrial;
                  return (
                    <tr key={client._id}>
                      {/* Logo */}
                      <td style={S.td}>
                        <div style={{ width: '56px', height: '40px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                          <img
                            src={`${API_BASE}${client.logoPath}`}
                            alt={client.name}
                            style={{ maxWidth: '48px', maxHeight: '32px', objectFit: 'contain' }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      </td>
                      {/* Name */}
                      <td style={{ ...S.td, fontWeight: 600, color: '#0f172a', fontSize: '13px', maxWidth: '240px' }}>
                        {client.name}
                      </td>
                      {/* Type */}
                      <td style={S.td}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '2px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
                          {meta.label}
                        </span>
                      </td>
                      {/* Website */}
                      <td style={{ ...S.td, fontSize: '12px' }}>
                        {client.website ? (
                          <a href={client.website} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', display: 'flex', alignItems: 'center', gap: '5px', textDecoration: 'none', fontWeight: 500 }}>
                            <Globe size={12} /> Visit
                          </a>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      {/* Order */}
                      <td style={{ ...S.td, fontSize: '12px', color: '#94a3b8' }}>{client.order ?? 0}</td>
                      {/* Status */}
                      <td style={S.td}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '2px', fontSize: '11px', fontWeight: 700, background: client.active ? '#f0fdf4' : '#f8fafc', color: client.active ? '#16a34a' : '#94a3b8', border: `1px solid ${client.active ? '#bbf7d0' : '#e2e8f0'}` }}>
                          <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: client.active ? '#16a34a' : '#94a3b8' }} />
                          {client.active ? 'Active' : 'Hidden'}
                        </span>
                      </td>
                      {/* Actions */}
                      <td style={S.td}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => setModal(client)}
                            style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', borderRadius: '2px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                          >
                            <Pencil size={12} /> Edit
                          </button>
                          <button
                            onClick={() => setDeleting(client)}
                            style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '2px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: '8px' }}>
          {filtered.length} client{filtered.length !== 1 ? 's' : ''} shown
        </p>
      </div>

      {/* ── MODALS ── */}
      {(modal === 'add' || (modal && typeof modal === 'object')) && (
        <ClientModal
          client={modal === 'add' ? null : modal}
          token={token}
          onSave={handleSaved}
          onClose={() => setModal(null)}
        />
      )}
      {deleting && (
        <DeleteDialog
          client={deleting}
          token={token}
          onDelete={handleDeleted}
          onClose={() => setDeleting(null)}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        select:focus, input:focus { outline: 2px solid #2563eb; outline-offset: -1px; }
        tr:hover td { background: #f0f7ff !important; }
      `}</style>
    </div>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const styles = {
  page:    { minHeight: '100vh', background: '#f1f5f9', fontFamily: "-apple-system,'Segoe UI',Arial,sans-serif" },

  nav:      { background: '#080e1c', borderBottom: '1px solid rgba(255,255,255,0.07)', position: 'sticky', top: 0, zIndex: 100 },
  navInner: { maxWidth: '1400px', margin: '0 auto', padding: '0 24px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navLeft:  { display: 'flex', alignItems: 'center', gap: '14px' },
  navDivider: { width: '1px', height: '18px', background: 'rgba(255,255,255,0.12)' },
  navLabel: { color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
  navChip:  { background: 'rgba(37,99,235,0.18)', border: '1px solid rgba(37,99,235,0.3)', color: '#93bbfd', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '2px', letterSpacing: '0.06em' },
  iconBtn:  { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, padding: '6px 14px', borderRadius: '4px', cursor: 'pointer' },

  content:  { maxWidth: '1400px', margin: '0 auto', padding: '28px 24px 48px' },

  tableWrap: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '8px' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:        { padding: '11px 16px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  td:        { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },

  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '64px 0' },
  empty:   { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '64px 0', textAlign: 'center' },
};

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  card:    { background: '#fff', borderRadius: '8px', boxShadow: '0 24px 80px rgba(0,0,0,0.25)', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', fontFamily: "-apple-system,'Segoe UI',Arial,sans-serif" },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #f1f5f9' },
  headerLabel: { margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' },
  closeBtn:    { background: '#f1f5f9', border: 'none', borderRadius: '6px', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' },

  label:     { display: 'block', fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' },
  input:     { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', color: '#0f172a', fontFamily: 'inherit', background: '#f8fafc', boxSizing: 'border-box' },

  cancelBtn: { padding: '9px 18px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#374151', cursor: 'pointer' },
  saveBtn:   { display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 22px', background: '#0a0f1e', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer' },
};
